import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { getConsent, saveConsent, OPEN_COOKIE_PREFERENCES_EVENT } from '../lib/analytics';

const Toggle = ({ enabled, onChange }) => (
    <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-[#CEAC8F]' : 'bg-muted'}`}
    >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
);

const CookieBanner = () => {
    const [visible, setVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [preferences, setPreferences] = useState({
        essential: true,
        analytics: false,
        marketing: false,
    });

    // Tags themselves are loaded by src/lib/analytics.js (initAnalytics) — the
    // banner only collects and saves the choice.
    useEffect(() => {
        const saved = getConsent();
        if (!saved) setVisible(true);
        else setPreferences(saved);
    }, []);

    // "Cookie Settings" in the footer reopens the preferences panel
    useEffect(() => {
        const open = () => {
            const saved = getConsent();
            if (saved) setPreferences(saved);
            setShowPreferences(true);
            setVisible(true);
        };
        window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, open);
        return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, open);
    }, []);

    const savePreferences = (prefs) => {
        saveConsent(prefs);
        setVisible(false);
        setShowPreferences(false);
    };

    const acceptAll = () => {
        const all = { essential: true, analytics: true, marketing: true };
        setPreferences(all);
        savePreferences(all);
    };

    const declineAll = () => {
        const essential = { essential: true, analytics: false, marketing: false };
        setPreferences(essential);
        savePreferences(essential);
    };

    const saveSelected = () => savePreferences(preferences);

    if (!visible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border shadow-2xl transition-colors duration-300">
            {!showPreferences ? (
                <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">
                        We use cookies to offer a seamless browsing experience, support essential services such as secure checkout, and better understand how our site is used. You may accept all cookies, reject non-essential cookies, or tailor your preferences. For more details, see our{' '}
                        <Link to="/privacy" className="text-[#CEAC8F] hover:underline">Privacy Policy</Link>.
                    </p>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                        <button
                            onClick={() => setShowPreferences(true)}
                            className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm transition-colors px-4 py-2 border border-border rounded-lg hover:border-[#CEAC8F]/30"
                        >
                            <Settings className="w-3 h-3" />
                            Preferences
                        </button>
                        <button
                            onClick={declineAll}
                            className="text-muted-foreground hover:text-foreground text-sm transition-colors px-4 py-2 border border-border rounded-lg hover:border-[#CEAC8F]/30"
                        >
                            Decline
                        </button>
                        <button
                            onClick={acceptAll}
                            className="bg-[#CEAC8F] text-black text-sm font-medium px-6 py-2 rounded-lg hover:bg-[#d4b896] transition-colors"
                        >
                            Accept All
                        </button>
                    </div>
                </div>
            ) : (
                <div className="max-w-2xl mx-auto px-6 py-6">
                    <h3 className="text-foreground font-medium mb-1">Cookie Preferences</h3>
                    <p className="text-muted-foreground text-xs mb-6">Choose which cookies you allow. Essential cookies cannot be disabled as they are required for the site to function.</p>

                    <div className="space-y-4 mb-6">
                        <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-lg">
                            <div>
                                <p className="text-foreground text-sm font-medium">Essential</p>
                                <p className="text-muted-foreground text-xs mt-1">Required for checkout, cart, and login. Cannot be disabled.</p>
                            </div>
                            <Toggle enabled={true} onChange={() => {}} />
                        </div>

                        <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-lg">
                            <div>
                                <p className="text-foreground text-sm font-medium">Analytics</p>
                                <p className="text-muted-foreground text-xs mt-1">Helps us understand how visitors use our site, via Google Analytics.</p>
                            </div>
                            <Toggle
                                enabled={preferences.analytics}
                                onChange={(val) => setPreferences(p => ({ ...p, analytics: val }))}
                            />
                        </div>

                        <div className="flex items-start justify-between gap-4 p-4 bg-muted/30 rounded-lg">
                            <div>
                                <p className="text-foreground text-sm font-medium">Marketing</p>
                                <p className="text-muted-foreground text-xs mt-1">Lets Klaviyo and Meta personalise emails and ads and measure campaign performance.</p>
                            </div>
                            <Toggle
                                enabled={preferences.marketing}
                                onChange={(val) => setPreferences(p => ({ ...p, marketing: val }))}
                            />
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowPreferences(false)}
                            className="flex-1 py-3 border border-border rounded-lg text-muted-foreground hover:text-foreground text-sm transition-colors"
                        >
                            Back
                        </button>
                        <button
                            onClick={saveSelected}
                            className="flex-1 py-3 bg-[#CEAC8F] text-black rounded-lg text-sm font-medium hover:bg-[#d4b896] transition-colors"
                        >
                            Save Preferences
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CookieBanner;